import { useTranslation } from "react-i18next";
import { useOperationsMap } from "../OperationsMapProvider";
import { Stack, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { useCallback, useMemo } from "react";
import { useDevice } from "../../../../providers/DeviceProvider";

const useStyles = makeStyles()(
  (theme) => ({
    root: {
      padding: theme.spacing(1),
      paddingTop: theme.spacing(1.5),
      paddingBottom: theme.spacing(6)
    },
    card: {
      background: theme.palette.background.default,
      padding: theme.spacing(2),
      borderRadius: theme.spacing(2),
      boxShadow: `0 0 1px 0 rgba(0, 0, 0, 0.04), 0 4px 4px 0 rgba(0, 0, 0, 0.02)`,
      width: "100%",
      position: "relative",
      cursor: "pointer",
      "&:after": {
        zIndex: 3,
        position: "absolute",
        height: "100%",
        width: "100%",
        top: "0px",
        left: "0px",
        opacity: 0,
        background: theme.palette.action.hover,
        borderRadius: theme.spacing(2),
        content: "''",
        display: "block",
        pointerEvents: "none"
      },
      "&:hover:after": {
        opacity: 1
      }
    },
    cardSlim: {
      background: theme.palette.background.default,
      padding: theme.spacing(2),
      borderRadius: theme.spacing(2),
      boxShadow: `0 0 1px 0 rgba(0, 0, 0, 0.04), 0 4px 4px 0 rgba(0, 0, 0, 0.02)`,
      width: "100%",
      position: "relative",
      cursor: "pointer",
    },
    highlight: {
      "&:after": {
        opacity: 1
      }
    }

  })
);

export default function VehiclesPanel() {
  const { classes } = useStyles();
  const { slim } = useDevice();

  const {
    vehicleLayerHoverId,
    operationsMap,
    openVehicleDialog,
    vehicles
  } = useOperationsMap();

  const onItemMouseEnter = useCallback((_, vehicle) => {
    operationsMap?.getPlugin("vehicles")?.setVehicleHighlight(vehicle.id);
  }, [operationsMap]);

  const onItemMouseLeave = useCallback(() => {
    operationsMap?.getPlugin("vehicles")?.setVehicleHighlight("none");
  }, [operationsMap]);

  const onItemClicked = useCallback((ev, vehicle) => {
    ev.preventDefault();
    openVehicleDialog(vehicle.id);
  }, [openVehicleDialog]);

  const sortedVehicles = useMemo(
    () => vehicles
      .map(v => ({ ...v, activeState: v.tasks?.length > 0 ? 2 : !!v.driver?.id ? 1 : 0 }))
      .sort((a, b) => (b.activeState - a.activeState)),
    [vehicles]
  );

  return (
    <Stack spacing={1} className={classes.root}>
      {sortedVehicles.map((vehicle, key) => (
        <VehicleItem
          key={key}
          vehicle={vehicle}
          onClick={onItemClicked}
          highlight={vehicleLayerHoverId === vehicle.id}
          onMouseLeave={onItemMouseLeave}
          onMouseEnter={onItemMouseEnter}
          slim={slim}
        />))}
    </Stack>
  );
}



function VehicleItem(props) {
  const { vehicle, onClick, highlight, onMouseEnter, onMouseLeave, slim } = props;
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <Stack
      className={`${slim ? classes.cardSlim : classes.card} ${highlight ? classes.highlight : ""}`}
      onClick={ev => onClick(ev, vehicle)}
      onMouseEnter={ev => onMouseEnter(ev, vehicle)}
      onMouseLeave={ev => onMouseLeave(ev, vehicle)}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
        <Typography variant="body2" fontWeight={600}>{vehicle.title}</Typography>
        <Typography
          color={vehicle.tasks?.length > 0 ? "info" : !!vehicle.driver?.id ? "warning" : "textDisabled"}
          variant="caption"
          fontWeight={700}
        >
          {vehicle.tasks?.length > 0 ? t("vehicleItem:active") : !!vehicle.driver?.id ? t("vehicleItem:available") : t("vehicleItem:inactive")}
        </Typography>
      </Stack>

      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
        <Typography variant="body2" color={!!vehicle.driver?.name ? "textSecondary" : "textDisabled"}>{vehicle.driver?.name || t("vehicleItem:noDriver")}</Typography>
        {
          !!vehicle?.fleetUnitData ? (
            <Typography variant="caption" color="textDisabled" fontWeight={500}>
              {getFleetUnitStateText(t, vehicle.fleetUnitData?.state?.name)}
            </Typography>
          ) : null
        }
      </Stack>
    </Stack>
  )
}


export function getFleetUnitStateText(t, name) {
  switch (name) {
    case "standing": return t("vehicleItem:stateStanding");
    case "driving": return t("vehicleItem:stateDriving");

    default:
      return t("vehicleItem:stateNotFound");
  }
}


export function hasLocation(serviceVehicle) {
  if (!serviceVehicle || !serviceVehicle.lastReportedLocation || !serviceVehicle.lastReportedLocation.coords) return false;

  const coords = serviceVehicle.lastReportedLocation.coords;
  const lat = coords.latitude;
  const lng = coords.longitude;

  return (typeof lat === "number" && lat !== 0) && (typeof lng === "number" && lat !== 0);
}


export function minutesSinceLastLocation(serviceVehicle) {
  const ts = serviceVehicle.lastReportedLocation?.timestamp || 0;
  if (typeof ts !== "number") return 99999;
  return Math.floor((Date.now() - ts) / 60000.0);
}